
<template>
  <div class="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900/70 via-black/50 to-gray-900/70">
    <div class="max-w-7xl mx-auto">
      <!-- Loading state -->
      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="bg-red-900/20 border-l-4 border-red-600 p-4 rounded-lg" role="alert">
        <p class="font-bold text-red-200">Erreur</p>
        <p class="text-red-100">{{ error }}</p>
      </div>

      <div v-else-if="currentUser" class="space-y-6">
        <!-- En-tête du profil -->
        <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 relative box-glow animate-fadeIn">
          <div class="relative h-32 bg-gradient-to-r from-amber-900/80 to-black/90">
            <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
            <div class="absolute -bottom-12 left-8">
              <div class="w-24 h-24 bg-gradient-to-br from-black/90 to-amber-950/30 rounded-full border-4 border-amber-700/40 flex items-center justify-center shadow-xl">
                <span class="text-4xl text-amber-300">{{ userInitials }}</span>
              </div>
            </div>
          </div>
          <div class="pt-16 pb-6 px-8">
            <div class="flex justify-between items-start">
              <div>
                <h1 class="text-2xl font-bold text-amber-300">{{ userInfo.username }}</h1>
                <p class="text-amber-200/60">{{ userInfo.email }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations principales -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Colonne de gauche : Informations personnelles -->
          <div class="space-y-6 animate-slideUp" style="animation-delay: 100ms;">
            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  Informations personnelles
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="space-y-4 relative">
                  <div>
                    <label class="block text-sm font-medium text-amber-200">Nom d'utilisateur</label>
                    <p class="mt-1 text-amber-100 bg-black/40 px-4 py-2.5 rounded-md border border-amber-800/20">{{ userInfo.username }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-amber-200">Email</label>
                    <p class="mt-1 text-amber-100 bg-black/40 px-4 py-2.5 rounded-md border border-amber-800/20">{{ userInfo.email }}</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-amber-200">Date d'inscription</label>
                    <p class="mt-1 text-amber-100 bg-black/40 px-4 py-2.5 rounded-md border border-amber-800/20">{{ formattedDate }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  Sécurité
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="space-y-4 relative">
                  <button 
                    @click="showChangePasswordModal = true" 
                    class="w-full px-4 py-2.5 bg-gradient-to-r from-amber-700/80 to-amber-900/80 text-amber-300 rounded-lg hover:from-amber-700/90 hover:to-amber-900/90 transition-all duration-300 border border-amber-700/40 hover:-translate-y-0.5 flex items-center justify-center"
                  >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                    </svg>
                    Changer le mot de passe
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Colonne du milieu : Statistiques de jeu -->
          <div class="space-y-6 animate-slideUp" style="animation-delay: 200ms;">
            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
                  </svg>
                  Statistiques
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="grid grid-cols-2 gap-4 relative">
                  <div class="bg-black/60 p-4 rounded-lg text-center border border-amber-800/30 shadow-inner">
                    <p class="text-2xl font-bold text-amber-400">{{ userInfo.matchesPlayed ?? 0 }}</p>
                    <p class="text-sm text-amber-200/80">Matchs joués</p>
                  </div>
                  <div class="bg-black/60 p-4 rounded-lg text-center border border-amber-800/30 shadow-inner">
                    <p class="text-2xl font-bold text-amber-400">{{ userInfo.wins ?? userInfo.victories ?? 0 }}</p>
                    <p class="text-sm text-amber-200/80">Victoires</p>
                  </div>
                  <div class="bg-black/60 p-4 rounded-lg text-center border border-amber-800/30 shadow-inner">
                    <p class="text-2xl font-bold text-amber-400">
                      {{
                        userInfo.matchesPlayed > 0
                          ? Math.round(((userInfo.wins ?? userInfo.victories ?? 0) / userInfo.matchesPlayed) * 100) + '%'
                          : '0%'
                      }}
                    </p>
                    <p class="text-sm text-amber-200/80">Taux de victoire</p>
                  </div>
                  <div class="bg-black/60 p-4 rounded-lg text-center border border-amber-800/30 shadow-inner">
                    <p class="text-2xl font-bold text-amber-400">{{ userInfo.elo ?? 0 }}</p>
                    <p class="text-sm text-amber-200/80">ELO</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                  Performance
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="space-y-3 relative">
                  <!-- Participation -->
                  <div class="bg-black/60 p-4 rounded-lg border border-amber-800/30 shadow-inner">
                    <div class="flex justify-between items-center">
                      <span class="text-sm text-amber-200">Participation (30j)</span>
                      <span class="text-sm font-medium text-amber-300">{{ performanceStats.recentMatches }} matchs</span>
                    </div>
                  </div>
                  
                  <!-- Classement -->
                  <div class="bg-black/60 p-4 rounded-lg border border-amber-800/30 shadow-inner">
                    <div class="flex justify-between items-center">
                      <span class="text-sm text-amber-200">Classement</span>
                      <span class="text-sm font-medium text-amber-300">{{ performanceStats.ranking }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Colonne de droite : Armées préférées -->
          <div class="space-y-6 animate-slideUp" style="animation-delay: 300ms;">
            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"/>
                  </svg>
                  Armées préférées
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="space-y-4 relative">
                  <div v-for="(army, index) in favoriteArmies" :key="index" 
                    class="flex items-center p-3 rounded-lg border border-amber-800/20 shadow-inner"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                    <div class="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-black to-amber-950/30 rounded-full flex items-center justify-center mr-3 border border-amber-700/40">
                      <span class="text-amber-300 text-xl">{{ army.icon }}</span>
                    </div>
                    <div class="flex-1">
                      <p class="text-amber-300 font-medium">{{ army.armyName }}</p>
                      <p class="text-xs text-amber-200/60">{{ army.faction }} • {{ army.matchesCount }} matchs</p>
                      <div class="flex items-center mt-1 space-x-3">
                        <span class="text-xs text-green-400">{{ army.wins }}V</span>
                        <span class="text-xs text-red-400">{{ army.losses }}D</span>
                        <span class="text-xs text-amber-300">{{ Math.round(army.winRate * 100) }}%</span>
                      </div>
                    </div>
                  </div>
                  <p v-if="favoriteArmies.length === 0" class="text-amber-200/60 text-center py-4">
                    Aucune armée jouée
                  </p>
                </div>
              </div>
            </div>

            <!-- Historique des matchs récents -->
            <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow">
              <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
                <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
                <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Matchs récents
                </h2>
              </div>
              <div class="p-6 relative">
                <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
                <div class="space-y-4 relative">
                  <div v-for="(match, index) in recentMatches" :key="index" 
                    class="flex items-center p-3 rounded-lg border border-amber-800/20 shadow-inner"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <span class="text-amber-300 font-medium">{{ match.opponent }}</span>
                          <span :class="match.isVictory ? 'text-green-400' : 'text-red-400'" class="ml-2 text-sm">
                            {{ match.isVictory ? 'Victoire' : 'Défaite' }}
                          </span>
                        </div>
                        <span class="text-xs text-amber-200/60">{{ match.date }}</span>
                      </div>
                      <div class="flex justify-between mt-1">
                        <div class="flex items-center space-x-2">
                          <span class="text-xs text-amber-200/60">{{ match.army }}</span>
                          <span class="text-xs text-amber-400">vs</span>
                          <span class="text-xs text-amber-200/60">{{ match.opponentArmy }}</span>
                        </div>
                        <span class="text-xs text-amber-300 font-mono">{{ match.score }}</span>
                      </div>
                    </div>
                  </div>
                  <p v-if="recentMatches.length === 0" class="text-amber-200/60 text-center py-4">
                    Aucun match récent
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Message si non connecté -->
      <div v-else class="bg-amber-900/20 border-l-4 border-amber-600 p-6 rounded-lg animate-fadeIn" role="alert">
        <p class="font-bold text-amber-300 mb-2">Attention</p>
        <p class="text-amber-200">
          Vous devez être connecté pour voir cette page. 
          <router-link to="/login" class="underline font-semibold text-amber-400 hover:text-amber-300">
            Se connecter
          </router-link>
        </p>
      </div>
    </div>
  </div>
  <PasswordChangeModal v-if="showChangePasswordModal" :show="showChangePasswordModal" @close="showChangePasswordModal = false" />
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import UserService from '@/services/user.service';
import PasswordChangeModal from '../components/PasswordChangeModal.vue';

export default {
  name: 'ProfileView',
  components: {
    PasswordChangeModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const showChangePasswordModal = ref(false);
    const loading = ref(false);
    const error = ref(null);

    const userInfo = ref({
      username: '',
      email: '',
      createdAt: null,
      matchesPlayed: 0,
      wins: 0,
      elo: 0
    });

    const stats = ref({
      matchesPlayed: 0,
      victories: 0,
      winRate: '0%',
      points: 0,
      participationRate: '0%',
      ranking: 'Non classé',
      rankingPercentage: '0%'
    });

    const favoriteArmies = ref([]);
    const recentMatches = ref([]);
    const performanceStats = ref({
      participationRate: '0%',
      ranking: 'Non classé',
      rankingPercentage: '0%',
      totalMatches: 0,
      recentMatches: 0,
      winStreak: 0,
      bestWinStreak: 0,
      lossStreak: 0,
      averageScore: 0,
      averageOpponentScore: 0,
      mostPlayedFaction: 'Aucune',
      bestPerformingFaction: 'Aucune',
      eloGain: 0,
      eloLoss: 0,
      performanceTrend: 'STABLE'
    });

    // Computed properties
    const currentUser = computed(() => store.state.auth.user);
    const userInitials = computed(() => {
      if (!currentUser.value) return '';
      return currentUser.value.username
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    });

    const formattedDate = computed(() => {
      if (!userInfo.value.createdAt) return '';
      return new Date(userInfo.value.createdAt).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    });

    // Methods
    const loadUserProfile = async () => {
      try {
        loading.value = true;
        const response = await UserService.getUserProfileWithArmyStats();
        userInfo.value = {
          username: response.username,
          email: response.email,
          createdAt: response.createdAt,
          matchesPlayed: response.matchesPlayed ?? 0,
          wins: response.wins ?? response.victories ?? 0,
          elo: response.elo ?? 0
        };
        
        // Statistiques adaptées à SPEARHEAD
        stats.value = {
          matchesPlayed: response.matchesPlayed || 0,
          victories: response.wins || 0,
          winRate: response.winRatio ? Math.round(response.winRatio * 100) + '%' : '0%',
          points: response.elo || 0,
          participationRate: '0%', // À calculer si nécessaire
          ranking: 'Non classé', // À calculer si nécessaire
          rankingPercentage: '0%' // À calculer si nécessaire
        };
        
        // Armées préférées avec les nouvelles données
        favoriteArmies.value = response.favoriteArmies || [];
        
        // Statistiques de performance
        if (response.performanceStats) {
          performanceStats.value = {
            participationRate: response.performanceStats.participationRate || '0%',
            ranking: response.performanceStats.ranking || 'Non classé',
            rankingPercentage: response.performanceStats.rankingPercentage || '0%',
            totalMatches: response.performanceStats.totalMatches || 0,
            recentMatches: response.performanceStats.recentMatches || 0,
            winStreak: response.performanceStats.winStreak || 0,
            bestWinStreak: response.performanceStats.bestWinStreak || 0,
            lossStreak: response.performanceStats.lossStreak || 0,
            averageScore: response.performanceStats.averageScore || 0,
            averageOpponentScore: response.performanceStats.averageOpponentScore || 0,
            mostPlayedFaction: response.performanceStats.mostPlayedFaction || 'Aucune',
            bestPerformingFaction: response.performanceStats.bestPerformingFaction || 'Aucune',
            eloGain: response.performanceStats.eloGain || 0,
            eloLoss: response.performanceStats.eloLoss || 0,
            performanceTrend: response.performanceStats.performanceTrend || 'STABLE'
          };
        }
        
        // Matchs récents avec les nouvelles données
        recentMatches.value = (response.recentMatches || []).map(match => ({
          id: match.id,
          opponent: match.user1?.username === currentUser.value?.username ? match.user2?.username : match.user1?.username,
          army: match.user1?.username === currentUser.value?.username ? match.armee1 : match.armee2,
          opponentArmy: match.user1?.username === currentUser.value?.username ? match.armee2 : match.armee1,
          isVictory: match.user1?.username === currentUser.value?.username ? 
            (match.scoreUser1 > match.scoreUser2) : 
            (match.scoreUser2 > match.scoreUser1),
          date: new Date(match.date).toLocaleDateString('fr-FR'),
          score: match.user1?.username === currentUser.value?.username ? 
            `${match.scoreUser1} - ${match.scoreUser2}` : 
            `${match.scoreUser2} - ${match.scoreUser1}`
        }));
      } catch (err) {
        error.value = "Erreur lors du chargement du profil";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };


    const changePassword = async (oldPassword, newPassword) => {
      try {
        await UserService.changePassword(oldPassword, newPassword);
        showChangePasswordModal.value = false;
      } catch (err) {
        error.value = "Erreur lors du changement de mot de passe";
        console.error(err);
      }
    };

    onMounted(async () => {
      if (!currentUser.value) {
        router.push('/login');
        return;
      }
      await loadUserProfile();
    });

    return {
      currentUser,
      userInitials,
      formattedDate,
      showChangePasswordModal,
      userInfo,
      stats,
      favoriteArmies,
      recentMatches,
      performanceStats,
      loading,
      error,
      changePassword
    };
  }
};
</script>

<style scoped>
.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23D4AF37' fill-opacity='0.2'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23D4AF37' fill-opacity='0.1'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px;
}

.box-glow {
  box-shadow: 0 0 30px rgba(217, 119, 6, 0.15), 
              0 5px 20px rgba(0, 0, 0, 0.3), 
              inset 0 0 15px rgba(217, 119, 6, 0.05);
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes slideUp {
  0% { 
    opacity: 0;
    transform: translateY(30px);
  }
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-slideUp {
  animation: slideUp 0.8s ease-out forwards;
}
</style> 